import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
  updateOfferListKey,
  fetchOfferList,
  fetchOffersById,
  fetchPersonalisedOfferList,
  streamOfferList,
  getMinOffers,
} from 'actions/OfferActions'
import arrayShuffle from 'lib/array/arrayShuffle'
import memoize from 'lib/memoize/memoize'
import getOfferListKey from 'lib/offer/offerListKey'
import { without } from 'lib/array/arrayUtils'
import type { SetOptional } from 'type-fest'
import { useSearchAnalytics } from 'hooks/Analytics/useSearchAnalytics'
import { trackSearchResultsEvent } from 'analytics/snowplow/events/searchResultsEvents'
import type {
  Filter_1_0_0, Search_3_0_0,
  SearchContext_1_0_3,
  Sort_1_0_0,
} from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import type { RankingPreferences } from '@luxuryescapes/contract-search'
import { getCanRedeemLuxPlusBenefits, getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getAllOffers } from 'selectors/offerSelectors'
import config from 'constants/config'
import { ALL_HOTEL_TYPES, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { hasDisplayableOfferPrice } from 'lib/offer/pricingVisibility'
import { omitKeys } from '../../lib/object/objectUtils'

export interface Options {
  /**
   * For when the offer list is no longer needed, but still needs to be called to avoid violating the Rules of Hooks
   * */
  disabled?: boolean
  /**
   * Randomize the offer ids in the list
   * Will be stable for that set of offer ids
   */
  randomize?: boolean
  /**
   * An array of offer ids to explicitly exclude from the results
   */
  excludedIds?: Array<string>
  /**
   * Whether explicitly filter out sold-out offer ids from the list
   * This will use the meta-data to determine if they are sold out
   */
  filterSoldOut?: boolean
  /**
   * Whether check the availability of the offer on the client side
   */
  clientCheckAvailability?: boolean
  /**
   * Control the weights of the different ranking factors
   */
  functionScoreWeights?: RankingPreferences
  /**
   * Exclude offers that do not have a positive displayable price.
   * Defaults to enabled for SPP, LPC, and LPP offer lists.
   */
  filterUnpriced?: boolean

  minRequiredOffers?: number
  searchId?: string
  typeaheadId?: string
  includeAllBedbankWithSales?: boolean
}

export interface TrackingOptions {
  /**
   * Whether to disable tracking for this search
   */
  disable?: boolean
  searchEvent: Search_3_0_0
  /**
   * The search state that were used to generate the this search
   */
  searchContext: SearchContext_1_0_3
  filterContexts?: Array<Filter_1_0_0>
  sortContext?: Sort_1_0_0
  searchSessionId?: string
  ignoreChangedAttributes?: Array<keyof App.OfferListFilters>
}

/**
 * Memoize is used here as it provides a stable set of offer ids for that exact version of the array
 * This ensures that we always return the same randomised set throughout the life of the page
 */
const getRandomizedOfferIds = memoize((offerIds: Array<string>) => arrayShuffle(offerIds))

function shouldHideMemberOnlyOffer(offer: App.AnyOffer | undefined, canViewLuxPlusBenefits: boolean): boolean {
  return offer?.luxPlus?.access === 'memberOnly' && !canViewLuxPlusBenefits
}

const emptyOffersRecord: Record<string, App.AnyOffer> = {}
const emptyOfferErrors: App.OfferState['offerErrors'] = {}
const emptyOffersLoading: App.OfferState['offersLoading'] = {}

const PRICE_FILTER_DEFAULT_OFFER_TYPES = new Set<App.OfferListOfferType>([
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
])
const HOTEL_OFFER_TYPES = new Set<App.OfferListOfferType>(ALL_HOTEL_TYPES)

const emptyOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: true,
  error: null,
}

const emptyDisabledOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: false,
  error: null,
}

function useOfferList(
  filters: App.OfferListFilters | undefined,
  options: Options = {},
  trackingOptions?: TrackingOptions,
): App.OfferList {
  const {
    randomize,
    excludedIds,
    filterSoldOut,
    clientCheckAvailability,
    minRequiredOffers,
    searchId,
    typeaheadId,
    functionScoreWeights,
    filterUnpriced: filterUnpricedOption,
  } = options

  const streamResults = filters?.isStream
  const isHomesAndVillasScoped = filters?.holidayTypesScoped?.includes('Homes & Villas') ?? false
  const filterUnpriced = filterUnpricedOption ?? Boolean(
    !isHomesAndVillasScoped &&
    (filters?.useSvcAccommodation ||
      (filters?.offerTypes?.length &&
        filters.offerTypes.every(offerType => HOTEL_OFFER_TYPES.has(offerType)) &&
        filters.offerTypes.some(offerType => PRICE_FILTER_DEFAULT_OFFER_TYPES.has(offerType)))),
  )
  const listenerId = useId()
  const searchDisabled = options.disabled || !filters
  const dispatch = useAppDispatch()
  const { trackSearchEvent } = useSearchAnalytics({
    searchEvent: trackingOptions?.searchEvent,
    searchContext: trackingOptions?.searchContext,
    filterContexts: trackingOptions?.filterContexts,
    sortContext: trackingOptions?.sortContext,
    searchSessionId: trackingOptions?.searchSessionId,
  })

  const { listKey, trackedListKey } = useMemo(() => {
    if (filters) {
      return {
        listKey: getOfferListKey(filters),
        trackedListKey: getOfferListKey(omitKeys(trackingOptions?.ignoreChangedAttributes ?? [], filters)),
      }
    }
    return {
      listKey: '',
      trackedListKey: '',
    }
  }, [filters, trackingOptions?.ignoreChangedAttributes])

  const [tracked, setTracked] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (searchDisabled) return

    let timeoutId: NodeJS.Timeout | undefined
    if (!trackingOptions?.disable && trackingOptions?.searchContext && !tracked.has(trackedListKey)) {
      timeoutId = setTimeout(() => {
        trackSearchEvent()
        setTracked(prev => new Set([...prev, trackedListKey]))
      }, 100)
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedListKey, searchDisabled, trackingOptions?.disable])

  // Re-fetch when LuxPlus viewing status changes (e.g. after Free Preview auto-enrolment on
  // signup) so member-visible offers and member pricing appear without a page refresh.
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  // Fetch offer list
  useEffect(() => {
    if (searchDisabled) return

    if (streamResults) {
      dispatch(streamOfferList(filters, listenerId, minRequiredOffers, searchId, { typeaheadId, functionScoreWeights }))
    } else if (filters.lere) {
      dispatch(fetchPersonalisedOfferList(filters))
    } else {
      dispatch(fetchOfferList(filters, {
        clientCheckAvailability,
      }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey, searchDisabled, canViewLuxPlusBenefits])

  useEffect(() => {
    return () => {
      if (streamResults && !searchDisabled) {
        dispatch(updateOfferListKey({ offerListKey: listKey, listenerId }))
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey])

  const offerList = useAppSelector((state) => {
    if (searchDisabled) {
      return emptyDisabledOfferList
    }
    return state.offer.offerLists[listKey] ?? emptyOfferList
  })

  const offerListFilters = useAppSelector(state => state.offer.offerListFilterOptions[listKey])

  const trackedSearchResults = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (searchDisabled) return
    if (trackingOptions?.disable || !trackingOptions?.searchContext) return
    if (typeof offerList.offerCount !== 'number') return
    if (trackedSearchResults.current.has(trackedListKey)) return

    trackedSearchResults.current.add(trackedListKey)
    trackSearchResultsEvent({
      searchContext: trackingOptions.searchContext,
      filterContexts: trackingOptions.filterContexts,
      sortContext: trackingOptions.sortContext,
      searchSessionId: trackingOptions.searchSessionId,
    }, offerList.offerCount)
  }, [
    offerList.offerCount,
    searchDisabled,
    trackedListKey,
    trackingOptions?.disable,
    trackingOptions?.filterContexts,
    trackingOptions?.searchContext,
    trackingOptions?.searchSessionId,
    trackingOptions?.sortContext,
  ])

  useEffect(() => {
    if (minRequiredOffers && minRequiredOffers > 0) {
      dispatch(getMinOffers({ offerListKey: listKey, minOffers: minRequiredOffers }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [minRequiredOffers, offerList.fetching, offerListFilters])

  const metaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData[listKey])
  const allOffers = useAppSelector(state => streamResults || filterUnpriced ? getAllOffers(state) : emptyOffersRecord)
  const offerErrors = useAppSelector(state => state.offer.offerErrors ?? emptyOfferErrors)
  const offersLoading = useAppSelector(state => state.offer.offersLoading ?? emptyOffersLoading)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)

  const filteredOfferIds = useMemo(() => {
    let offerIds = excludedIds ? without(offerList.offerIds, ...excludedIds) : offerList.offerIds

    if (streamResults && !canRedeemLuxPlusBenefits && config.ENABLE_FREEPREVIEW_TO_NON_MEMBERS) {
      offerIds = offerIds.filter(id => !shouldHideMemberOnlyOffer(allOffers[id], canViewLuxPlusBenefits))
    }

    if (filterSoldOut && metaData) {
      offerIds = offerIds.filter(id => {
        const offerData = metaData[id]
        // if we don't have any meta data on it, assume it's available
        return !offerData || offerData.available
      })
    }

    return offerIds
  }, [allOffers, canRedeemLuxPlusBenefits, canViewLuxPlusBenefits, excludedIds, filterSoldOut, metaData, offerList.offerIds, streamResults])

  const offerIdsToHydrate = useMemo(() => {
    if (!filterUnpriced) return []

    return filteredOfferIds.filter(id => !allOffers[id] && !offerErrors[id] && !offersLoading[id])
  }, [allOffers, filterUnpriced, filteredOfferIds, offerErrors, offersLoading])

  useEffect(() => {
    if (!searchDisabled && offerIdsToHydrate.length > 0) {
      dispatch(fetchOffersById(offerIdsToHydrate))
    }
  }, [dispatch, offerIdsToHydrate, searchDisabled])

  const finalOfferList = useMemo(() => {
    const postExclusionIds = filterUnpriced ?
        filteredOfferIds.filter(id => hasDisplayableOfferPrice(allOffers[id])) :
      filteredOfferIds

    const fetchingOfferPrices = filterUnpriced &&
      filteredOfferIds.some(id => !allOffers[id] && !offerErrors[id])

    if (randomize) {
      return {
        ...offerList,
        key: offerList.key ?? listKey,
        fetching: offerList.fetching || fetchingOfferPrices,
        offerIds: getRandomizedOfferIds(postExclusionIds),
      }
    }
    return {
      ...offerList,
      key: offerList.key ?? listKey,
      fetching: offerList.fetching || fetchingOfferPrices,
      offerIds: postExclusionIds,
    }
  }, [allOffers, filterUnpriced, filteredOfferIds, offerErrors, offerList, randomize, listKey])
  return finalOfferList
}

export default useOfferList
